import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWalkthrough, WalkthroughSection } from '../../context/WalkthroughContext';
import { useSidebar } from '../../context/SidebarContext';
import { getUserData } from '../../utils/encryption';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  useTheme,
  Popper,
} from '@mui/material';
import UpgradeDialog from './UpgradeDialog';
import TrialExpiredDialog from './TrialExpiredDialog';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  TouchApp as TouchAppIcon,
  Visibility as VisibilityIcon,
  Dashboard,
  Folder,
  Extension,
  Code,
  Hub,
} from '@mui/icons-material';
import ForumIcon from '@mui/icons-material/Forum';
import { styled } from '@mui/material/styles';
import { WalkthroughStep, walkthroughSteps } from 'data/walkthrough-steps';
import { getGuidedTourStatus, GuidedTourStatus, updateGuidedTourStatus } from 'services/api';
import { error, log } from 'utils/logger';
import { getAccessToken } from 'services/tokenService';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    maxWidth: '400px',
    width: '90vw',
    maxHeight: '80vh',
    background: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.98)' : '#FFFFFF',
    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 0 30px rgba(0, 0, 0, 0.8)' 
      : '0 0 30px rgba(0, 0, 0, 0.15)',
    position: 'fixed',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden' // Prevent dialog-level scrolling
  }
}));

const HighlightOverlay = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  zIndex: 9998,
  pointerEvents: 'none',
}));

// Container for the highlight box that follows the element
const HighlightContainer = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 9999,
  pointerEvents: 'none',
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  position: 'absolute', // Changed from 'fixed' to 'absolute'
  border: `3px solid #FF4405`,
  borderRadius: '12px',
  backgroundColor: 'rgba(255, 68, 5, 0.1)',
  zIndex: 99999, // Significantly higher z-index to appear above dialogs
  pointerEvents: 'auto', // Allow clicks on the highlight box
  cursor: 'pointer', // Show pointer cursor on hover
  boxShadow: '0 0 20px rgba(255, 68, 5, 0.5), inset 0 0 20px rgba(255, 68, 5, 0.2)',
  animation: 'highlightPulse 2s infinite',
  '@keyframes highlightPulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
      borderColor: '#FF4405',
    },
    '50%': {
      transform: 'scale(1.02)',
      opacity: 0.8,
      borderColor: '#FF6B35',
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
      borderColor: '#FF4405',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-6px',
    left: '-6px',
    right: '-6px',
    bottom: '-6px',
    border: '2px solid rgba(255, 68, 5, 0.3)',
    borderRadius: '16px',
    animation: 'outerPulse 2s infinite',
  },
  '@keyframes outerPulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.7,
    },
    '50%': {
      transform: 'scale(1.1)',
      opacity: 0.3,
    },
    '100%': {
      transform: 'scale(1.2)',
      opacity: 0,
    },
  }
}));

const TooltipPopper = styled(Popper)(({ theme }) => ({
  zIndex: 10000,
  '& .MuiPaper-root': {
    borderRadius: '16px',
    background: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.98)' : '#FFFFFF',
    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 25px rgba(0, 0, 0, 0.4)' 
      : '0 8px 25px rgba(0, 0, 0, 0.15)',
    maxWidth: '350px',
  }
}));

const GuidedWalkthrough: React.FC = () => {
  const { showWalkthrough, setShowWalkthrough, currentStep, closeWalkthrough, completeWalkthrough, nextStep, setSelectedSection, setCurrentStep, isTrialExpired } = useWalkthrough();
  const { isOpen: sidebarIsOpen, setIsOpen: setSidebarOpen } = useSidebar();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlightElement, setHighlightElement] = useState<HTMLElement | null>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [waitingForAction, setWaitingForAction] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [tourStatus, setTourStatus] = useState<GuidedTourStatus | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Add state to track highlight box position
  const [highlightPosition, setHighlightPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0
  });
  const [showTrialExpiredDialog, setShowTrialExpiredDialog] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const steps: WalkthroughStep[] = walkthroughSteps;

  
  const currentStepData = steps[currentStep];
  
  // Set trial expired dialog state based on context
  useEffect(() => {
    if (isTrialExpired) {
      log('Trial is expired, showing trial expired dialog');
      setShowTrialExpiredDialog(true);
    } else {
      setShowTrialExpiredDialog(false);
    }
  }, [isTrialExpired, showWalkthrough]); // Also respond to changes in showWalkthrough

  // Fetch tour status from API
  const fetchTourStatus = useCallback(async () => {
    try {
      // Check if user is authenticated before making API call
      const authToken = getAccessToken();
      if (!authToken) {
        log('No authentication token found, skipping guided tour status fetch');
        // If no auth token, set default status and don't show walkthrough on login page
        if (window.location.pathname.includes('/login')) {
          setShowWalkthrough(false);
        }
        return;
      }

      // Trial expiration is now handled by the context and the effect above
      
      // Add a delay to ensure user data is fully loaded in localStorage after OAuth login
      // This helps ensure the auth headers (user_id and org_id) are available
      const user = getUserData();
      if (user) {
        try {
          // If user_id or org_names_list is missing, add a delay
          if (!user.user_id || !user.org_names_list) {
            log('User data incomplete, adding delay before fetching guided tour status');
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (e : any) {
          error('Error parsing user data:', e);
          // Add delay if there's an error parsing user data
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } else {
        // Add delay if user data is not found
        log('User data not found, adding delay before fetching guided tour status');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      log('Fetching guided tour status from API');
      const status = await getGuidedTourStatus();
      setTourStatus(status);
      log('Guided tour status loaded from API:', status);
      
    } catch (err : any) {
      error('Error fetching guided tour status from API:', err);
      // On error, don't show walkthrough on login page
      if (window.location.pathname.includes('/login')) {
        setShowWalkthrough(false);
      }
    }
  }, [setShowWalkthrough]);
  
  // Fetch tour status only once on component mount
  useEffect(() => {
    // Only fetch if we're not on the login page and haven't fetched yet
    if (!window.location.pathname.includes('/login') && !tourStatus) {
      fetchTourStatus();
    } else if (window.location.pathname.includes('/login')) {
      // Explicitly don't show walkthrough on login page
      setShowWalkthrough(false);
    }
  }, [tourStatus,fetchTourStatus,setShowWalkthrough]);

  // Only fetch tour status when walkthrough is first opened (not on every step change)
  useEffect(() => {
    if (showWalkthrough && !tourStatus) {
      // Check authentication before showing walkthrough
      const authToken = getAccessToken();
      if (!authToken && window.location.pathname.includes('/login')) {
        log('No authentication token found on login page, closing walkthrough');
        setShowWalkthrough(false);
        return;
      }
      fetchTourStatus();
    }
  }, [showWalkthrough, tourStatus,fetchTourStatus,setShowWalkthrough]);

  // Handle sidebar opening for navigation steps
  useEffect(() => {
    if (showWalkthrough && currentStepData.action === 'click' && 
        (currentStepData.targetSelector?.includes('href="/projects"') || 
         currentStepData.targetSelector?.includes('href="/integration"') ||
         currentStepData.targetSelector?.includes('href="/sast"') ||
         currentStepData.targetSelector?.includes('href*="ask"'))) {
      // Open sidebar for navigation steps using context
      if (!sidebarIsOpen && !sidebarOpened) {
        log('Opening sidebar for navigation step:', currentStepData.id);
        setSidebarOpen(true);
        setSidebarOpened(true);
      } else if (sidebarIsOpen) {
        log('Sidebar already open for navigation step:', currentStepData.id);
        setSidebarOpened(true); // Mark as opened so element detection works
      }
    }
  }, [currentStep, showWalkthrough, sidebarOpened, currentStepData, sidebarIsOpen, setSidebarOpen]);

  // Find and highlight target element
  useEffect(() => {
    if (!showWalkthrough || !currentStepData.targetSelector || currentStepData.skipHighlight) {
      setHighlightElement(null);
      setTooltipAnchor(null);
      setSidebarOpened(false);
      return;
    }

    const findElement = () => {
      if (!currentStepData.targetSelector) return null;
      
      log('Looking for element with selector:', currentStepData.targetSelector);
      
      // Try multiple selector strategies
      const selectors: string[] = [
        currentStepData.targetSelector,
        currentStepData.targetSelector.replace(/\\/g, ''), // Remove escapes
        // For navigation links, try simpler selectors
        ...(currentStepData.targetSelector.includes('href="/projects"') ? ['a[href="/projects"]'] : []),
        ...(currentStepData.targetSelector.includes('href="/integration"') ? ['a[href="/integration"]'] : []),
        ...(currentStepData.targetSelector.includes('href="/sast"') ? ['a[href="/sast"]'] : []),
        // Special case for SAST dashboard statistics cards
        ...(currentStepData.id === 'sast-dashboard' ? [
          // More specific selectors for StatisticsCards component
          '.MuiBox-root > .MuiBox-root > .MuiBox-root', // Target the statistics cards container
          'div[style*="display: grid"]', // Target elements with inline grid style
          'div[style*="grid-template-columns"]', // Target elements with grid template columns
          '[class*="grid"]', // Any grid container
          '[class*="StatisticsCards"]', // Component class name
          // Target the Box that contains the StatisticsCards component
          'div:has(> div > div > svg)', // Box containing cards with icons
          // Try to find by position - usually at the top of the page
          ...Array.from(document.querySelectorAll('.MuiBox-root')).filter(el => {
            const rect = el.getBoundingClientRect();
            return rect.top < 300 && rect.width > 500; // Near the top with significant width
          }).map(el => `#${el.id}, .${Array.from(el.classList).join('.')}`),
          // Try to find by content - look for elements containing the card labels
          ...Array.from(document.querySelectorAll('*')).filter(el => 
            (el.textContent || '').includes('Total Scans') || 
            (el.textContent || '').includes('Identified Fixes') ||
            (el.textContent || '').includes('Completed Fixes')
          ).map(el => {
            // Get the parent container that might be the grid
            const parent = el.parentElement?.parentElement?.parentElement;
            return parent ? `#${parent.id}, .${Array.from(parent.classList).join('.')}` : '';
          }).filter(Boolean)
        ] : []),
        // Special case for Ask Ana navigation which uses /help path in the sidebar
        ...(currentStepData.id === 'ask-ana-navigation' ? [
          'a[href="/help"]', 
          'a[href*="help"]',
          'a[href*="ask"]',
          'a[href*="ana"]',
          // Try to find by text content
          ...Array.from(document.querySelectorAll('a')).filter(a => 
            (a.textContent || '').toLowerCase().includes('ask') || 
            (a.textContent || '').toLowerCase().includes('ana')
          ).map(a => `#${a.id}, .${Array.from(a.classList).join('.')}`),
          // Try to find by parent element with ForumIcon
          ...Array.from(document.querySelectorAll('svg')).filter(svg => 
            svg.classList.contains('MuiSvgIcon-root') && 
            (svg.dataset.testid === 'ForumIcon' || svg.innerHTML.includes('Forum'))
          ).map(svg => {
            const parent = svg.closest('a');
            return parent ? `#${parent.id}, .${Array.from(parent.classList).join('.')}` : '';
          }).filter(Boolean)
        ] : []),
        // Special case for Ask Ana welcome screen
        ...(currentStepData.id === 'ask-ana-welcome' ? [
          '[class*="text-center"]', 
          '[class*="welcome"]',
          'h1, h2, h3, h4, h5, h6',
          '.MuiPaper-root',
          // Try to find by text content
          ...Array.from(document.querySelectorAll('*')).filter(el => 
            (el.textContent || '').toLowerCase().includes('ai security expert') || 
            (el.textContent || '').toLowerCase().includes('ask ana')
          ).map(el => `#${el.id}, .${Array.from(el.classList).join('.')}`)
        ] : []),
        // Special case for Ask Ana chat input
        ...(currentStepData.id === 'ask-ana-chat' ? [
          'input[placeholder*="Ask"]', 
          'textarea[placeholder*="Ask"]', 
          '[class*="input"]',
          'input[type="text"]',
          'textarea',
          // Try to find by position - usually at the bottom of the page
          ...Array.from(document.querySelectorAll('input, textarea')).filter(el => {
            const rect = el.getBoundingClientRect();
            return rect.bottom > window.innerHeight - 200; // Near the bottom of the viewport
          }).map(el => `#${el.id}, .${Array.from(el.classList).join('.')}`)
        ] : []),
        // Special case for Ask Ana history menu
        ...(currentStepData.id === 'ask-ana-history' ? [
          'button[class*="menu"]', 
          '[class*="history"]', 
          'button:has([class*="menu"])',
          // Try to find menu buttons
          ...Array.from(document.querySelectorAll('button')).filter(btn => {
            const svg = btn.querySelector('svg');
            return svg && (
              svg.classList.contains('MuiSvgIcon-root') || 
              btn.getAttribute('aria-label')?.toLowerCase().includes('menu') ||
              btn.getAttribute('title')?.toLowerCase().includes('menu')
            );
          }).map(btn => `#${btn.id}, .${Array.from(btn.classList).join('.')}`)
        ] : []),
        `[data-testid="${currentStepData.id}"]`, // Fallback to test id
        // Additional fallbacks for project form fields
        ...(currentStepData.id === 'project-name-field' ? [
          'input[name="name"]', 
          'input[placeholder*="name" i]',
          'input[label*="name" i]',
          '.MuiDialog-paper input:first-of-type',
          '.MuiDialogContent-root input:first-of-type'
        ] : []),
        ...(currentStepData.id === 'project-description-field' ? [
          'input[name="description"]', 
          'textarea[name="description"]',
          'input[placeholder*="description" i]',
          'textarea[placeholder*="description" i]',
          '.MuiDialog-paper textarea',
          '.MuiDialogContent-root textarea'
        ] : []),
        ...(currentStepData.id === 'project-repo-field' ? [
          'input[name="repository"]',
          'input[name="repo"]',
          'input[placeholder*="repository" i]',
          'input[placeholder*="repo" i]',
          'input[placeholder*="url" i]',
          '.MuiDialog-paper input[type="url"]',
          '.MuiDialogContent-root input:nth-of-type(2)'
        ] : []),
      ];

      for (const selector of selectors) {
        try {
          // Skip empty or invalid selectors
          if (!selector || selector.includes('null') || selector === '#' || selector.trim() === '') {
            log('Skipping invalid selector:', selector);
            continue;
          }
          
          // Clean up selectors that might have invalid parts
          const cleanSelector = selector
            .split(',')
            .map(part => part.trim())
            .filter(part => part && part !== '#' && !part.includes('null'))
            .join(', ');
            
          if (!cleanSelector) {
            continue;
          }
          
          log('Trying selector:', cleanSelector);
          const element = document.querySelector(cleanSelector) as HTMLElement;
          if (element && element.offsetParent !== null) { // Check if visible
            log('Found element:', element);
            setHighlightElement(element);
            setTooltipAnchor(element);
            return element;
          }
        } catch (e) {
          log('Selector failed:', selector, e);
          // Invalid selector, continue
        }
      }

      // Fallback: try to find by text content - comprehensive search
      log('Trying comprehensive DOM search for:', currentStepData.actionText);
      
      // First, let's see what's actually in the DOM
      log('=== DOM ANALYSIS ===');
      const allElements = Array.from(document.querySelectorAll('*'));
      const drawerElements = allElements.filter(el => {
        const className = el.className?.toString() || '';
        return className.includes('Drawer') || className.includes('drawer');
      });
      log('Drawer-related elements:', drawerElements.length);
      drawerElements.forEach((el, i) => {
        if (i < 5) log(`Drawer ${i}:`, el.className, el.tagName);
      });
      
      const muiElements = allElements.filter(el => {
        const className = el.className?.toString() || '';
        return className.includes('Mui');
      });
      log('MUI elements:', muiElements.length);
      
      // Look for all links on the page
      const allLinks = Array.from(document.querySelectorAll('a'));
      log('All links on page:', allLinks.length);
      allLinks.forEach((link, i) => {
        if (i < 15) {
          log(`Link ${i}:`, {
            text: link.textContent?.trim(),
            href: link.href,
            className: link.className,
            parent: link.parentElement?.className
          });
        }
      });
      
      // Look for all buttons
      const allButtons = Array.from(document.querySelectorAll('button'));
      log('All buttons on page:', allButtons.length);
      allButtons.forEach((btn, i) => {
        if (i < 10) {
          log(`Button ${i}:`, {
            text: btn.textContent?.trim(),
            className: btn.className,
            onclick: btn.onclick?.toString().substring(0, 50)
          });
        }
      });
      
      // For navigation steps, look for specific text
      let searchText = '';
      if (currentStepData.id === 'projects-navigation') searchText = 'projects';
      else if (currentStepData.id === 'integrations-navigation') searchText = 'integration';
      else if (currentStepData.id === 'sbom-navigation') searchText = 'sbom';
      else if (currentStepData.id === 'sast-navigation') searchText = 'sast';
      else if (currentStepData.id === 'new-project-button') searchText = 'new project';
      else if (currentStepData.id === 'ask-ana-navigation' || 
               currentStepData.id === 'ask-ana-welcome' || 
               currentStepData.id === 'ask-ana-chat' || 
               currentStepData.id === 'ask-ana-history') searchText = 'ask ana';
      else if (currentStepData.actionText) {
        searchText = currentStepData.actionText.toLowerCase().split(' ')[0];
      }
      
      if (searchText) {
        log('Searching for text:', searchText);
        
        // Search all clickable elements
        const clickableElements = Array.from(document.querySelectorAll('a, button, [role="button"], [onclick], [class*="nav"], [class*="menu"]'));
        log('Total clickable elements:', clickableElements.length);
        
        const matchingElements = clickableElements.filter(el => {
          const text = el.textContent?.toLowerCase() || '';
          const href = (el as HTMLAnchorElement).href || '';
          const className = el.className?.toString() || '';
          
          return text.includes(searchText) || href.includes(searchText) || className.includes(searchText);
        });
        
        log('Matching elements found:', matchingElements.length);
        matchingElements.forEach((el, i) => {
          log(`Match ${i}:`, {
            element: el,
            text: el.textContent?.trim(),
            href: (el as HTMLAnchorElement).href,
            className: el.className,
            visible: (el as HTMLElement).offsetParent !== null,
            parent: el.parentElement?.className
          });
        });
        
        // Find the first visible matching element
        const element = matchingElements.find(el => 
          (el as HTMLElement).offsetParent !== null
        ) as HTMLElement;
        
        if (element) {
          log('Selected element:', element);
          setHighlightElement(element);
          setTooltipAnchor(element);
          return element;
        }
      }

      log('No element found for step:', currentStepData.id);
      return null;
    };

    // For navigation steps, wait a bit for sidebar to fully render
    // If sidebar is already open, use shorter delay
    const isNavigationStep = currentStepData.action === 'click' && 
      (currentStepData.targetSelector?.includes('href="/projects"') || 
       currentStepData.targetSelector?.includes('href="/integration"') ||
       currentStepData.targetSelector?.includes('href="/sast"') ||
       currentStepData.targetSelector?.includes('href*="ask"'));
    
    // Determine appropriate delay based on step type
    let delay = 100;
    
    if (isNavigationStep) {
      delay = sidebarIsOpen ? 500 : 1200; // Increased delays for navigation steps
    } else if (currentStepData.id === 'project-form' || 
               currentStepData.id === 'project-name-field' || 
               currentStepData.id === 'project-description-field' || 
               currentStepData.id === 'project-repo-field' || 
               currentStepData.id === 'project-tags-field') {
      // Longer delay for dialog and form fields to ensure they're fully rendered
      delay = 1500;
    } else if (currentStepData.id === 'projects-overview' || 
               currentStepData.id === 'github-integration' || 
               currentStepData.id === 'third-party-tools' ||
               currentStepData.id === 'sast-dashboard' ||
               currentStepData.id === 'sast-scan-history' ||
               currentStepData.id === 'ask-ana-welcome' ||
               currentStepData.id === 'ask-ana-chat' ||
               currentStepData.id === 'ask-ana-history') {
      // Steps that appear after page navigation need longer delays for data to populate
      delay = 1000;
    } else if (currentStepData.id === 'sast-scan-details' || 
               currentStepData.id === 'sast-fix-buttons') {
      // Steps on scan details pages need extra time for scan data to load
      delay = 1500;
    }
    
    const searchTimer = setTimeout(() => {
      const element = findElement();
      
      if (!element && (currentStepData.waitForElement || currentStepData.action === 'click' || 
                       currentStepData.id === 'project-name-field' || 
                       currentStepData.id === 'project-description-field' || 
                       currentStepData.id === 'project-repo-field' || 
                       currentStepData.id === 'project-tags-field')) {
        log('Element not found immediately, starting interval search for:', currentStepData.id);
        // Wait for element to appear
        intervalRef.current = setInterval(() => {
          const foundElement = findElement();
          if (foundElement) {
            log('Found element via interval for step:', currentStepData.id);
            clearInterval(intervalRef.current!);
            intervalRef.current = null;
          }
        }, 300);
      }
    }, delay);

    console.log(searchTimer);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentStep, showWalkthrough, location.pathname, currentStepData,sidebarIsOpen]);

  // Check for walkthrough resume after navigation to scan details page
  useEffect(() => {
    // First check if user is authenticated
    const authToken = getAccessToken();
    if (!authToken) {
      log('No authentication token found, not resuming walkthrough');
      return;
    }
    
    // Check if we're on a scan details page (URL contains "/sast/scan/")
    const isScanDetailsPage = location.pathname.includes('/sast/scan/');
    
    // Check if we need to resume the walkthrough
    const shouldResume = localStorage.getItem('walkthrough-resume') === 'true';
    const nextStepFromStorage = localStorage.getItem('walkthrough-next-step');
    
    log('CHECKING WALKTHROUGH RESUMPTION:');
    log(`- Current path: ${location.pathname}`);
    log(`- Is scan details page: ${isScanDetailsPage}`);
    log(`- Should resume: ${shouldResume}`);
    log(`- Next step from storage: ${nextStepFromStorage}`);
    
    // Make sure we're on a scan details page with a specific scan ID, not just "/sast/scan"
    // This ensures we only resume when we have a valid scan ID in the URL
    const hasScanId = /\/sast\/scan\/\d+/.test(location.pathname);
    
    if (isScanDetailsPage && shouldResume && hasScanId) {
      log('Detected scan details page with ID, resuming walkthrough');
      
      // Clear the resume flags immediately to prevent multiple resumptions
      localStorage.removeItem('walkthrough-resume');
      localStorage.removeItem('walkthrough-next-step');
      
      // First, make sure any dialogs are closed immediately
      const closeAllDialogs = () => {
        // Find all dialogs
        const allDialogs = document.querySelectorAll('[role="dialog"], .MuiDialog-paper');
        log(`Found ${allDialogs.length} dialogs to close before resuming walkthrough`);
        
        // Try multiple approaches to close any open dialogs
        allDialogs.forEach((dialog, index) => {
          log(`Attempting to close dialog ${index}`);
          
          // 1. Try to find a close button in the dialog
          const closeButtons = dialog.querySelectorAll('button');
          let buttonClicked = false;
          
          // Convert NodeList to Array to avoid TypeScript error
          Array.from(closeButtons).forEach(button => {
            // Look for buttons that might be close buttons
            if (button.textContent?.includes('Close') || 
                button.getAttribute('aria-label')?.includes('close') ||
                button.classList.contains('MuiDialogActions-root') ||
                button.classList.contains('MuiIconButton-root')) {
              log('Found potential close button, clicking it');
              button.click();
              buttonClicked = true;
            }
          });
          
          // 2. If no close button found, try clicking outside the dialog
          if (!buttonClicked) {
            const backdrop = document.querySelector('.MuiBackdrop-root');
            if (backdrop) {
              log('Clicking backdrop to close dialog');
              (backdrop as HTMLElement).click();
            }
          }
          
          // 3. As a last resort, try to remove the dialog from the DOM
          if (!buttonClicked) {
            try {
              log('Attempting to remove dialog from DOM');
              dialog.parentNode?.removeChild(dialog);
              
              // Also remove any backdrops
              const backdrops = document.querySelectorAll('.MuiBackdrop-root');
              backdrops.forEach(backdrop => {
                backdrop.parentNode?.removeChild(backdrop);
              });
            } catch (e) {
              error('Error removing dialog:', e);
            }
          }
        });
      };
      
      // Close any dialogs immediately
      closeAllDialogs();
      
      // Find the index of the sast-scan-details step (the intermediate step)
      const scanDetailsStepIndex = steps.findIndex(step => step.id === 'sast-scan-details');
      
      // Fallback to hardcoded index if not found
      const stepIndex = scanDetailsStepIndex !== -1 ? scanDetailsStepIndex : 14;
      
      log(`Using step index for scan details: ${stepIndex}`);
      log(`Step ID at index ${stepIndex}: ${steps[stepIndex]?.id}`);
      
      // Resume the walkthrough at the next step after a delay
      setTimeout(() => {
        // Close dialogs again just to be sure
        closeAllDialogs();
        
        // Then resume the walkthrough
        log('RESUMING WALKTHROUGH:');
        log(`- Setting current step to: ${stepIndex}`);
        log(`- Step ID: ${steps[stepIndex]?.id}`);
        log(`- Step title: ${steps[stepIndex]?.title}`);
        
        // Force set the current step to the scan details step
        setCurrentStep(stepIndex);
        setShowWalkthrough(true);
        
        // Add data-walkthrough attribute to scan items for easier targeting
        const addDataAttributeToScanItems = () => {
          // Target all rows in the scan details table
          const scanItems = document.querySelectorAll('tr');
          let scanItemsFound = 0;
          
          scanItems.forEach(item => {
            if (!item.hasAttribute('data-walkthrough')) {
              item.setAttribute('data-walkthrough', 'scan-item');
              scanItemsFound++;
            }
          });
          
          if (scanItemsFound > 0) {
            log(`Added data-walkthrough attribute to ${scanItemsFound} scan items`);
          }
        };
        
        // Add data attributes immediately
        addDataAttributeToScanItems();
        
        // And also set up an interval to keep trying in case the items load later
        const addAttributeInterval = setInterval(() => {
          addDataAttributeToScanItems();
        }, 500);
        
        // Clear interval after 10 seconds
        setTimeout(() => {
          clearInterval(addAttributeInterval);
        }, 10000);
        
        // Look for scan items to highlight
        setTimeout(() => {
          // Try multiple selectors to find scan items
          const scanItemSelectors = [
            '[data-walkthrough="scan-item"]',
            'tr',
            'tbody tr',
            '.MuiTableRow-root'
          ];
          
          let scanItems: NodeListOf<Element> | null = null;
          
          // Try each selector until we find items
          for (const selector of scanItemSelectors) {
            try {
              const items = document.querySelectorAll(selector);
              if (items.length > 0) {
                log(`Found ${items.length} scan items with selector: ${selector}`);
                scanItems = items;
                break;
              }
            } catch (e) {
              log(`Error with selector ${selector}:`, e);
            }
          }
          
          if (scanItems && scanItems.length > 0) {
            log('Found scan items:', scanItems.length);
            
            // Set the first scan item as the highlight element
            const firstScanItem = scanItems[0] as HTMLElement;
            setHighlightElement(firstScanItem);
            setTooltipAnchor(firstScanItem);
          } else {
            log('No scan items found, will keep looking');
            
            // Set up an interval to keep looking for scan items
            const findScanItemInterval = setInterval(() => {
              for (const selector of scanItemSelectors) {
                try {
                  const items = document.querySelectorAll(selector);
                  if (items.length > 0) {
                    log(`Found ${items.length} scan items with selector: ${selector}`);
                    
                    // Set the first scan item as the highlight element
                    const firstScanItem = items[0] as HTMLElement;
                    setHighlightElement(firstScanItem);
                    setTooltipAnchor(firstScanItem);
                    
                    clearInterval(findScanItemInterval);
                    break;
                  }
                } catch (e) {
                  // Ignore errors with selectors
                }
              }
            }, 500);
            
            // Clear interval after 10 seconds to prevent infinite checking
            setTimeout(() => {
              clearInterval(findScanItemInterval);
            }, 10000);
          }
        }, 500);
      }, 500); // Reduced delay to make the transition faster
    }
  }, [location.pathname, setShowWalkthrough, setCurrentStep, steps]);

  // Handle navigation
  useEffect(() => {
    if (showWalkthrough && currentStepData && currentStepData.action !== 'click') {
      // Special case for Ask Ana navigation which can have multiple valid routes
      if (currentStepData.id === 'ask-ana-welcome' || 
          currentStepData.id === 'ask-ana-chat' || 
          currentStepData.id === 'ask-ana-history') {
        if (location.pathname !== '/help' && location.pathname !== '/ask-ana') {
          navigate('/help'); // Navigate to the actual route that works
        }
      } else if (currentStepData.route.includes(':id')) {
        // Handle dynamic routes with :id parameter
        const scanIdMatch = location.pathname.match(/\/sast\/scan\/(\d+)/);
        if (scanIdMatch && scanIdMatch[1]) {
          // We're already on a scan details page with an ID, no need to navigate
          log(`Already on scan details page with ID: ${scanIdMatch[1]}`);
        } else {
          // We're not on a scan details page with an ID, so don't try to navigate
          // This prevents navigation to /sast/scan/:id which would result in a blank page
          log('Not navigating to dynamic route without an ID');
        }
      } else if (currentStepData.route !== location.pathname) {
        navigate(currentStepData.route);
      }
    }
  }, [currentStep, showWalkthrough, navigate, location.pathname, currentStepData]);

  // Auto-skip navigation steps if already on target page
  useEffect(() => {
    if (showWalkthrough && currentStepData.action === 'click' && highlightElement) {
      // Check if this is a navigation step and we're already on the target page
      // const isNavigationStep = currentStepData.id === 'integrations-navigation' ||
      //                         currentStepData.id === 'sast-navigation';
      
      // // Exclude 'projects-navigation' and 'ask-ana-navigation' from auto-advancing
      // if (isNavigationStep) {
      //   // Check if we're already on the target page
      //   const targetRoute = currentStepData.id === 'integrations-navigation' ? '/integration' :
      //                      currentStepData.id === 'sast-navigation' ? '/sast' : null;
        
      //   if (targetRoute && location.pathname === targetRoute) {
      //     log('Already on target page, auto-advancing:', currentStepData.id);
      //     setHighlightElement(null);
      //     setTooltipAnchor(null);
      //     setTimeout(() => {
      //       handleNext();
      //     }, 1500);
      //     return;
      //   }
      // }
      
      // Special handling for projects-navigation and ask-ana-navigation - don't auto-advance even if already on page
      if ((currentStepData.id === 'projects-navigation' && location.pathname === '/projects') ||
          (currentStepData.id === 'ask-ana-navigation' && location.pathname === '/help') ||
          (currentStepData.id === 'sast-navigation' && location.pathname === '/sast') ||
          (currentStepData.id === 'sbom-navigation' && location.pathname === '/sbom') ||
          (currentStepData.id === 'integrations-navigation' && location.pathname === '/integration')) {
        log(`On ${currentStepData.id === 'projects-navigation' ? 'projects' : 'ask ana'} page, but not auto-advancing to allow user interaction`);
        // Don't auto-advance, let user interact with the page
      }
    }
  }, [showWalkthrough, currentStepData, highlightElement, location.pathname]);

  // We're not auto-advancing for ask-ana-navigation, similar to projects-navigation
  // This allows the user to interact with the page before moving to the next step

  // Check for dialog presence when on new-project-button step
  useEffect(() => {
    if (showWalkthrough && currentStepData.id === 'new-project-button') {
      // Check if dialog is already open
      const dialogCheckInterval = setInterval(() => {
        const dialogElement = document.querySelector('[role="dialog"], .MuiDialog-paper');
        if (dialogElement) {
          log('Dialog detected while on new-project-button step, advancing to next step');
          clearInterval(dialogCheckInterval);
          
          // Clear current highlights
          setHighlightElement(null);
          setTooltipAnchor(null);
          setWaitingForAction(false);
          
          // Advance to next step
          setTimeout(() => {
            nextStep();
            
            // Set dialog as highlight element for next step
            setTimeout(() => {
              const dialogAfterAdvance = document.querySelector('[role="dialog"], .MuiDialog-paper');
              if (dialogAfterAdvance) {
                log('Setting dialog as highlight element after auto-advancing');
                setHighlightElement(dialogAfterAdvance as HTMLElement);
                setTooltipAnchor(dialogAfterAdvance as HTMLElement);
              }
            }, 200);
          }, 100);
        }
      }, 500);
      
      return () => {
        clearInterval(dialogCheckInterval);
      };
    }
  }, [showWalkthrough, currentStepData.id, nextStep]);


  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      log('Advancing from step', currentStep, 'to', currentStep + 1);
      // Clear current highlights immediately
      setHighlightElement(null);
      setTooltipAnchor(null);
      setWaitingForAction(false);
      setSidebarOpened(false); // Reset sidebar state for next step
      
      // Advance to next step
      nextStep();
      
    }
  }, [currentStep, steps.length, nextStep]);

  // Handle click and type actions
  useEffect(() => {
    if ((currentStepData.action === 'click' || currentStepData.action === 'type') && highlightElement && !waitingForAction) {
      setWaitingForAction(true);
      
      if (currentStepData.action === 'click') {
        log('Setting up click handler for step:', currentStepData.id);
        
// Special handling for sast-scan-history step
          if (currentStepData.id === 'sast-scan-history') {
            log('SAST scan history step - setting up special handling');
            
            // For sast-scan-history, we'll use a different approach
            // Instead of preventing the default action, we'll let the click pass through
            // and advance to the next step after a delay to allow navigation to complete
            
            const handleScanHistoryClick = (e: Event) => {
              log('Scan history item clicked - closing walkthrough to allow navigation');
              // Don't prevent default or stop propagation
              
              // Force close any dialogs immediately
              const forceCloseDialogs = () => {
                // First, try to find all dialogs on the page
                const allDialogs = document.querySelectorAll('[role="dialog"], .MuiDialog-paper, [class*="MuiModal"], [class*="modal"]');
                log(`Found ${allDialogs.length} dialogs on the page`);
                
                if (allDialogs.length === 0) {
                  log('No dialogs found, nothing to close');
                  return;
                }
                
                // Try multiple approaches to close any open dialogs
                allDialogs.forEach((dialog, index) => {
                  log(`Attempting to close dialog ${index}`);
                  
                  // 1. Try to find a close button in the dialog
                  const closeButtons = dialog.querySelectorAll('button');
                  let buttonClicked = false;
                  
                  // Convert NodeList to Array to avoid TypeScript error
                  Array.from(closeButtons).forEach(button => {
                    // Look for buttons that might be close buttons
                    if (button.textContent?.includes('Close') || 
                        button.getAttribute('aria-label')?.includes('close') ||
                        button.classList.contains('MuiDialogActions-root') ||
                        button.classList.contains('MuiIconButton-root') ||
                        button.classList.contains('MuiButtonBase-root')) {
                      log('Found potential close button, clicking it');
                      button.click();
                      buttonClicked = true;
                    }
                  });
                  
                  // 2. If no close button found, try clicking outside the dialog
                  if (!buttonClicked) {
                    const backdrop = document.querySelector('.MuiBackdrop-root');
                    if (backdrop) {
                      log('Clicking backdrop to close dialog');
                      (backdrop as HTMLElement).click();
                    }
                  }
                  
                  // 3. As a last resort, try to remove the dialog from the DOM
                  try {
                    log('Attempting to remove dialog from DOM');
                    dialog.parentNode?.removeChild(dialog);
                    
                    // Also remove any backdrops
                    const backdrops = document.querySelectorAll('.MuiBackdrop-root');
                    backdrops.forEach(backdrop => {
                      backdrop.parentNode?.removeChild(backdrop);
                    });
                  } catch (e) {
                    error('Error removing dialog:', e);
                  }
                });
                
                // 4. Try to click the "Browse your scan history" button if it exists
                const browseButtons = document.querySelectorAll('button:contains("Browse your scan history")');
                Array.from(browseButtons).forEach(button => {
                  log('Found Browse button, clicking it');
                  (button as HTMLElement).click();
                });
                
                // 5. Try to find and click any element that might close the dialog
                const closeElements = document.querySelectorAll('[aria-label*="close"], [class*="close"], [class*="Close"]');
                Array.from(closeElements).forEach(el => {
                  log('Found close element, clicking it');
                  (el as HTMLElement).click();
                });
              };
              
              // Try to close dialogs immediately
              forceCloseDialogs();
              
              // Try again after a short delay
              setTimeout(forceCloseDialogs, 100);
              
              // And again after a longer delay
              setTimeout(forceCloseDialogs, 300);
              
              // Completely close the walkthrough to allow navigation
              // We'll detect when we're on the scan details page and show the next step
              closeWalkthrough();
              
              // Find the index of the sast-fix-buttons step
              const fixButtonsStepIndex = steps.findIndex(step => step.id === 'sast-fix-buttons');
              
              // Fallback to hardcoded index if not found
              const nextStepIndex = fixButtonsStepIndex !== -1 ? fixButtonsStepIndex : 14;
              
              // Store the current step and next step in localStorage
              localStorage.removeItem('walkthrough-last-step');
              localStorage.removeItem('walkthrough-next-step');
              localStorage.removeItem('walkthrough-resume');
              
              // Set fresh values to avoid any stale data
              localStorage.setItem('walkthrough-next-step', String(nextStepIndex));
              localStorage.setItem('walkthrough-resume', 'true');
              
              // Directly trigger a click on the scan history item to ensure navigation happens
              const scanHistoryItem = e.target as HTMLElement;
              const clickableParent = scanHistoryItem.closest('tr') || scanHistoryItem;
              
              log('WALKTHROUGH STATE SET FOR RESUMPTION:');
              log(`- Next step index: ${nextStepIndex}`);
              log(`- Resume flag: true`);
              log('Waiting for navigation to scan details page...');
              
              // Ensure the click propagates to trigger navigation
              setTimeout(() => {
                log('Triggering click on scan history item');
                clickableParent.click();
              }, 100);
            };
            
            // Add click listener with capture: true to ensure it fires before other handlers
            highlightElement.addEventListener('click', handleScanHistoryClick, { once: true });
            
            return () => {
              highlightElement.removeEventListener('click', handleScanHistoryClick);
            };
          }
        
        // Normal handling for other steps
        const handleClick = (e: Event) => {
          log('Click detected for step:', currentStepData.id);
          e.preventDefault();
          e.stopPropagation();
          
          // Clear highlights immediately
          setHighlightElement(null);
          setTooltipAnchor(null);
          setWaitingForAction(false);
          
          // For dialog-opening buttons or navigation steps, wait longer for content to load
          let delay = 100;
          
          // Check if this is the projects-navigation step - don't auto-advance
          if (currentStepData.id === 'projects-navigation') {
            log('Projects navigation step - waiting for user interaction');
            // Don't auto-advance, let user interact with the projects page
            return;
          } else if (currentStepData.id === 'sast-dashboard') {
            log('SAST dashboard step - advancing to next step');
            // Force immediate advance to next step for SAST dashboard
            handleNext();
            return;
          } else if (currentStepData.id === 'new-project-button') {
            log('New project button clicked - advancing to form step after delay');
            delay = 300; // Shorter initial delay
            
            // Trigger the actual element click to open the dialog
            highlightElement.click();
            
            // Force immediate advance to next step
            log('Immediately advancing to next step after new-project-button click');
            handleNext();
            
            // Set up an interval to check for the dialog and highlight it
            const dialogCheckInterval = setInterval(() => {
              const dialogElement = document.querySelector('[role="dialog"], .MuiDialog-paper');
              if (dialogElement) {
                log('Dialog found in interval check, highlighting it');
                clearInterval(dialogCheckInterval);
                
                // Set the dialog as the highlight element
                setHighlightElement(dialogElement as HTMLElement);
                setTooltipAnchor(dialogElement as HTMLElement);
                setWaitingForAction(false);
              }
            }, 200);
            
            // Clear interval after 5 seconds to prevent infinite checking
            setTimeout(() => {
              clearInterval(dialogCheckInterval);
            }, 5000);
            
            return; // Don't proceed with the normal flow
        } else if (currentStepData.id === 'integrations-navigation' || 
                  currentStepData.id === 'sast-navigation' ||
                  currentStepData.id === 'sbom-navigation' ||
                  currentStepData.id === 'ask-ana-navigation') {
          delay = 1500; // Other navigation steps
        }
        
        setTimeout(() => {
          log('Advancing to next step after click with delay:', delay);
          handleNext();
        }, delay);
        };
 
        // Add click listener for non-sast-scan-history steps
        if (currentStepData.id !== 'sast-scan-history') {
          highlightElement.addEventListener('click', handleClick, { once: true });
          
          return () => {
            highlightElement.removeEventListener('click', handleClick);
          };
        }
      } else if (currentStepData.action === 'type') {
        // For type actions, wait for user to type something
        const handleInput = (e: Event) => {
          const target = e.target as HTMLInputElement | HTMLTextAreaElement;
          if (target.value.length > 0) {
            setWaitingForAction(false);
            
            // Clear highlights after typing
            setTimeout(() => {
              setHighlightElement(null);
              setTooltipAnchor(null);
              handleNext();
            }, 1000); // Give user time to see they typed something
          }
        };

        highlightElement.addEventListener('input', handleInput);
        
        return () => {
          highlightElement.removeEventListener('input', handleInput);
        };
      }
    }
  }, [highlightElement, currentStepData.action, waitingForAction, currentStepData.id, closeWalkthrough, handleNext, steps]);

  const handleComplete = useCallback(async (section: string) => {
    // Get current section
    const currentSection = section;
    
    // Map section to status field
    const statusMapping: Record<string, string> = {
      'dashboard-projects': 'tour_dashboard_status',
      'integration': 'tour_integration_status',
      'scan-using-ana': 'tour_scan_status',
      'fix-by-ana': 'tour_fix_status',
      'ask-ana': 'tour_askana_status'
    };
    
    const statusField = statusMapping[currentSection as string];
    
    // Update status to completed in API
    if (statusField) {
      try {
        await updateGuidedTourStatus(statusField, 'completed');
        log(`Updated ${statusField} to completed in API`);
        
        // Update local state to reflect the change immediately
        setTourStatus(prevStatus => {
          if (!prevStatus) return prevStatus;
          return {
            ...prevStatus,
            [statusField]: 'completed'
          };
        });
      } catch (err : any) {
        error(`Error updating ${statusField} in API:`, err);
      }
    }
    
    completeWalkthrough();
    // navigate('/dashboard');
  }, [completeWalkthrough]);

  const handleInProgress = async (section: string) => {
    log("CALLING tour api")
    // Get current section
    const currentSection = section;
    log(currentSection)
    
    // Map section to status field
    const statusMapping: Record<string, string> = {
      'dashboard-projects': 'tour_dashboard_status',
      'integration': 'tour_integration_status',
      'scan-using-ana': 'tour_scan_status',
      'fix-by-ana': 'tour_fix_status',
      'ask-ana': 'tour_askana_status'
    };
    
    const statusField = statusMapping[currentSection as string];
    
    // Update status to in_progress in API
    if (statusField) {
      try {
        await updateGuidedTourStatus(statusField, 'in_progress');
        log(`Updated ${statusField} to in_progress in API`);
        
        // Update local state to reflect the change immediately
        setTourStatus(prevStatus => {
          if (!prevStatus) return prevStatus;
          return {
            ...prevStatus,
            [statusField]: 'in_progress'
          };
        });
      } catch (err : any) {
        error(`Error updating ${statusField} in API:`, err);
      }
    }
  };

  // Check for project creation success message
  useEffect(() => {
    if (showWalkthrough && (currentStepData.id === 'create-project-button' || currentStepData.id === 'project-form')) {
      // Check for success message
      const successCheckInterval = setInterval(() => {
        // Simplest approach: check all elements on the page for success text
        const allElements = document.body.querySelectorAll('*');
        let successFound = false;
        
        // Look for any element containing success text
        allElements.forEach(el => {
          const text = el.textContent?.toLowerCase() || '';
          if (text.includes('success') || 
              text.includes('created successfully') || 
              text.includes('project created')) {
            successFound = true;
          }
        });
        
        // Also check for the specific success box in the screenshot
        const successBox = document.querySelector('.MuiBox-root');
        if (successBox && successBox.textContent?.includes('Success')) {
          successFound = true;
        }
        
        // Check for the specific success message shown in the screenshot
        if (document.body.textContent?.includes('Project created successfully')) {
          successFound = true;
        }
        
        // Additional checks for project creation success
        // Check if we're back on the projects page (indicating successful creation)
        if (window.location.pathname === '/projects') {
          // Check if there are any new projects in the list
          const projectCards = document.querySelectorAll('[class*="project"], [class*="Project"], .MuiCard-root');
          if (projectCards.length > 0) {
            successFound = true;
          }
        }
        
        // Check for toast notifications indicating success
        const toastElements = document.querySelectorAll('[class*="toast"], [class*="Toast"], [class*="snackbar"], [class*="Snackbar"]');
        toastElements.forEach(toast => {
          const toastText = toast.textContent?.toLowerCase() || '';
          if (toastText.includes('success') || toastText.includes('created')) {
            successFound = true;
          }
        });
        
        if (successFound) {
          log('Project creation success detected, completing section');
          clearInterval(successCheckInterval);
          
          // Clear current highlights
          setHighlightElement(null);
          setTooltipAnchor(null);
          setWaitingForAction(false);
          
          // Get current section and complete it
          const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
          
          // Complete the section instead of just advancing to next step
          setTimeout(() => {
            log(`Auto-completing section: ${currentSection} due to project creation success`);
            handleComplete(currentSection);
          }, 1000);
        }
      }, 500);
      
      return () => {
        clearInterval(successCheckInterval);
      };
    }
  }, [showWalkthrough, currentStepData.id, handleComplete, currentStepData.section]);

  // Check for ANA fix dialog opening (for auto fix issues section)
  useEffect(() => {
    if (showWalkthrough && currentStepData.id === 'sast-fix-buttons') {
      // Check for ANA solution dialog opening
      const dialogCheckInterval = setInterval(() => {
        // Look for ANA solution dialog
        const anaDialog = document.querySelector('[class*="AnaSolutionDialog"], [class*="ana-dialog"], [id*="ana-dialog"]');
        let dialogFound = false;
        
        if (anaDialog) {
          dialogFound = true;
        }
        
        // Also check for any dialog with ANA-related content
        const allDialogs = document.querySelectorAll('[role="dialog"], .MuiDialog-paper');
        allDialogs.forEach(dialog => {
          const dialogText = dialog.textContent?.toLowerCase() || '';
          if (dialogText.includes('ana') || 
              dialogText.includes('solution') || 
              dialogText.includes('fix') ||
              dialogText.includes('vulnerability')) {
            dialogFound = true;
          }
        });
        
        // Check for any modal or backdrop that might indicate a dialog opened
        const backdrop = document.querySelector('.MuiBackdrop-root');
        if (backdrop) {
          dialogFound = true;
        }
        
        if (dialogFound) {
          log('ANA fix dialog detected, completing section');
          clearInterval(dialogCheckInterval);
          
          // Clear current highlights
          setHighlightElement(null);
          setTooltipAnchor(null);
          setWaitingForAction(false);
          
          // Get current section and complete it
          const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
          
          // Complete the section instead of just advancing to next step
          setTimeout(() => {
            log(`Auto-completing section: ${currentSection} due to ANA fix dialog opening`);
            handleComplete(currentSection);
          }, 1000);
        }
      }, 500);
      
      return () => {
        clearInterval(dialogCheckInterval);
      };
    }
  }, [showWalkthrough, currentStepData.id, handleComplete, currentStepData.section]);

  // Check for SBOM generation completion (for scan-using-ana section)
  useEffect(() => {
    if (showWalkthrough && currentStepData.id === 'generate-sbom-button') {
      // Check for navigation to SBOM scan details page or completion indicators
      const sbomCheckInterval = setInterval(() => {
        // Check if we've navigated to an SBOM scan details page
        const isOnScanDetailsPage = window.location.pathname.includes('/sbom/scan/');
        
        // Check for success indicators
        const successElements = document.querySelectorAll('*');
        let successFound = false;
        
        successElements.forEach(el => {
          const text = el.textContent?.toLowerCase() || '';
          if (text.includes('generating') || 
              text.includes('scan started') || 
              text.includes('sbom generated') ||
              text.includes('analysis in progress')) {
            successFound = true;
          }
        });
        
        // Check for loading states that indicate scan has started
        const loadingElements = document.querySelectorAll('[class*="loading"], [class*="progress"], .MuiCircularProgress-root');
        const hasLoadingIndicators = loadingElements.length > 0;
        
        if (isOnScanDetailsPage || successFound || hasLoadingIndicators) {
          log('SBOM generation detected (navigation or success indicators), completing section');
          clearInterval(sbomCheckInterval);
          
          // Clear current highlights
          setHighlightElement(null);
          setTooltipAnchor(null);
          setWaitingForAction(false);
          
          // Get current section and complete it
          const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
          
          // Complete the section
          setTimeout(() => {
            log(`Auto-completing section: ${currentSection} due to SBOM generation`);
            handleComplete(currentSection);
          }, 500);
        }
      }, 500);
      
      return () => {
        clearInterval(sbomCheckInterval);
      };
    }
  }, [showWalkthrough, currentStepData.id, handleComplete, currentStepData.section]);

  // Update highlight position when element changes
  useEffect(() => {
    if (highlightElement) {
      const rect = highlightElement.getBoundingClientRect();
      setHighlightPosition({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16
      });
    }
  }, [highlightElement]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, steps]);

  // Add scroll event listener to update highlight box position
  useEffect(() => {
    if (!highlightElement || !showWalkthrough || currentStepData.skipHighlight) {
      return;
    }

    // Store the element reference to avoid closure issues
    const element = highlightElement;
    
    // Track the last position to avoid unnecessary updates
    let lastPosition = {
      top: 0,
      left: 0,
      width: 0,
      height: 0
    };
    
    // Throttle function to limit how often the update runs
    const throttle = (func: Function, limit: number) => {
      let inThrottle: boolean = false;
      return function() {
        if (!inThrottle) {
          func();
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    };

    // Function to update highlight box position
    const updateHighlightPosition = () => {
      if (!element || !element.isConnected) return;
      
      try {
        const rect = element.getBoundingClientRect();
        
        // Check if element is actually visible
        if (rect.width === 0 && rect.height === 0) return;
        
        const newPosition = {
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        };
        
        // Only update if position has changed significantly and element is still in viewport
        if ((Math.abs(newPosition.top - lastPosition.top) > 5 || 
            Math.abs(newPosition.left - lastPosition.left) > 5) &&
            rect.top >= -100 && rect.left >= -100 && 
            rect.top <= window.innerHeight + 100 && 
            rect.left <= window.innerWidth + 100) {
          lastPosition = newPosition;
          setHighlightPosition(newPosition);
        }
      } catch (err : any) {
        error("Error updating highlight position:", err);
      }
    };
    
    // Initial update with delay to ensure element is stable
    const initialTimer = setTimeout(updateHighlightPosition, 100);
    
    // Throttled event handlers with higher priority
    const handleScroll = throttle(() => {
      // Use requestAnimationFrame for smoother scrolling
      window.requestAnimationFrame(updateHighlightPosition);
    }, 50);
    
    const handleResize = throttle(updateHighlightPosition, 200);
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Reduce frequency of periodic checks to prevent jitter
    const intervalId = setInterval(updateHighlightPosition, 500);
    
    // Clean up event listeners and interval
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearInterval(intervalId);
    };
  }, [highlightElement, showWalkthrough, currentStepData?.skipHighlight]);

  
  // If walkthrough is not showing, don't render anything
  // If walkthrough is not showing, don't render anything
  if (!showWalkthrough) return null;
  
  // If trial is expired, only show the trial expired dialog
  if (isTrialExpired) {
    return (
      <>
        {/* Trial Expired Dialog */}
        <TrialExpiredDialog
          open={showTrialExpiredDialog && showWalkthrough} // Only show when walkthrough is active
          onClose={() => {
            setShowTrialExpiredDialog(false);
            closeWalkthrough();
          }}
          onLearnMore={() => {
            setShowTrialExpiredDialog(false);
            setShowUpgradeDialog(true);
          }}
        />

{/* Upgrade Dialog */}
      <UpgradeDialog 
        open={showUpgradeDialog} 
        onClose={() => {
          setShowUpgradeDialog(false);
          closeWalkthrough(); // Close walkthrough when upgrade dialog is closed
        }} 
      />
      </>
    );
  }

  const getActionIcon = () => {
    switch (currentStepData.action) {
      case 'click':
        return <TouchAppIcon sx={{ fontSize: 16 }} />;
      case 'type':
        return <PlayArrowIcon sx={{ fontSize: 16 }} />;
      case 'view':
        return <VisibilityIcon sx={{ fontSize: 16 }} />;
      default:
        return <PlayArrowIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <>
      {/* Backdrop overlay */}
      <HighlightOverlay />

      {/* Element highlight */}
      {highlightElement && !currentStepData.skipHighlight && (
        <HighlightContainer>
          <HighlightBox
            onClick={(e) => {
              // Special handling for sast-scan-history and sast-scan-details steps
              if (currentStepData.id === 'sast-scan-history' || currentStepData.id === 'sast-scan-details') {
                log(`${currentStepData.id} clicked - allowing click to pass through`);
                
                // For sast-scan-details, we need to directly trigger the click on the underlying element
                if (currentStepData.id === 'sast-scan-details') {
                  // Don't prevent default or stop propagation
                  // This allows the click to pass through to the underlying element
                  
                  // Find the actual Box element that controls the dropdown
                  // This is the parent element with the onClick handler that toggles the dropdown
                  const findingGroupBox = highlightElement.closest('[role="button"]') || 
                                          highlightElement.closest('.MuiBox-root') ||
                                          highlightElement;
                  
                  log('Found finding group box:', findingGroupBox);
                  
                  // Create and dispatch a native click event to ensure it bypasses React's event system
                  const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                  });
                  
                  // Dispatch the click event directly on the element
                  findingGroupBox.dispatchEvent(clickEvent);
                  
                  // Advance to next step after a delay to let the user see the dropdown open
                  setTimeout(() => {
                    log('Advancing to next step after opening collapsible group');
                    handleNext();
                  }, 1000);
                  
                  return;
                }
                
                // Don't prevent default or stop propagation
                return;
              }
              
              // For all other steps, use the original behavior
              e.preventDefault();
              e.stopPropagation();
              
              if (currentStepData.action === 'click') {
                // Clear highlights immediately
                setHighlightElement(null);
                setTooltipAnchor(null);
                
                // Special handling for new-project-button
                if (currentStepData.id === 'new-project-button') {
                  // Trigger the actual element click to open the dialog
                  highlightElement.click();
                  
                  // Immediately advance to next step
                  log('Immediately advancing to next step after new-project-button click');
                  handleNext();
                  
                  // Set up an interval to check for the dialog and highlight it
                  const dialogCheckInterval = setInterval(() => {
                    const dialogElement = document.querySelector('[role="dialog"], .MuiDialog-paper');
                    if (dialogElement) {
                      log('Dialog found in interval check, highlighting it');
                      clearInterval(dialogCheckInterval);
                      
                      // Set the dialog as the highlight element
                      setHighlightElement(dialogElement as HTMLElement);
                      setTooltipAnchor(dialogElement as HTMLElement);
                    }
                  }, 200);
                  
                  // Clear interval after 5 seconds to prevent infinite checking
                  setTimeout(() => {
                    clearInterval(dialogCheckInterval);
                  }, 5000);
                  
                  return;
                }
                
                // For other click actions
                highlightElement.click();
                
                // Navigate to next step
                setTimeout(() => {
                  handleNext();
                }, 300);
              }
            }}
            sx={{
              position: 'absolute',
              // Use the tracked position state instead of calculating inline
              top: highlightPosition.top,
              left: highlightPosition.left,
              width: highlightPosition.width,
              height: highlightPosition.height,
              // Make the highlight box non-interactive for certain steps
              pointerEvents: currentStepData.id === 'sast-scan-history' || currentStepData.id === 'sast-fix-buttons' ? 'none' : 'auto',
            }}
          />
        </HighlightContainer>
      )}

      {/* Tooltip for highlighted elements */}
      {tooltipAnchor && !currentStepData.skipHighlight && (
        <TooltipPopper
          open={true}
          anchorEl={tooltipAnchor}
          placement={currentStepData.position || 'bottom'}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 20],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 20,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom', 'top', 'right', 'left'],
              },
            },
          ]}
        >
          <Paper sx={{ p: 3, maxWidth: '350px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 28, 
                  height: 28, 
                  borderRadius: '6px', 
                  backgroundColor: '#FF440520',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FF4405'
                }}>
                  {getActionIcon()}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  {currentStepData.title}
                </Typography>
              </Box>
              <IconButton 
                onClick={closeWalkthrough} 
                size="small" 
                sx={{ 
                  color: '#94A3B8',
                  padding: '4px',
                  marginRight: '-8px',
                  marginTop: '-8px'
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Typography variant="body2" sx={{ 
              color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151',
              mb: 2,
              lineHeight: 1.5
            }}>
              {currentStepData.description}
            </Typography>

            {currentStepData.actionText && (
              <Paper sx={{ 
                p: 1.5, 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 68, 5, 0.1)' : 'rgba(255, 68, 5, 0.05)',
                border: '1px solid rgba(255, 68, 5, 0.2)',
                borderRadius: '8px',
                mb: 1.5
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#FF4405',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.875rem'
                }}>
                  {getActionIcon()}
                  {currentStepData.actionText}
                </Typography>
              </Paper>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                {(() => {
                  // Get current section
                  const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
                  
                  // Filter steps by current section
                  const sectionSteps = steps.filter(step => {
                    const stepSection = typeof step.section === 'string' ? step.section : step.section;
                    return stepSection === currentSection;
                  });
                  
                  // Find position of current step within section steps
                  const sectionStepIndex = sectionSteps.findIndex(step => step.id === currentStepData.id);
                  
                  // Return step count for this section
                  return `Step ${sectionStepIndex + 1} of ${sectionSteps.length}`;
                })()}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Always show Next/Finish button for the last step of each section */}
                {(currentStepData.action !== 'click' || 
                  (currentStepData.id === 'projects-navigation' && location.pathname === '/projects') || 
                  (currentStepData.id === 'ask-ana-navigation' && location.pathname === '/help') ||
                  (currentStepData.id === 'sast-navigation' && location.pathname === '/sast') ||
                  (currentStepData.id === 'sbom-navigation' && location.pathname === '/sbom') ||
                  (currentStepData.id === 'integrations-navigation' && location.pathname === '/integration') ||
                  currentStepData.id === 'ask-ana-history' ||
                  currentStepData.id === 'generate-sbom-button') && (
                  <Button
                    onClick={() => {
                      // Get current section
                      const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
                      log(`curr section ${currentSection}`)
                      
                      // Filter steps by current section
                      const sectionSteps = steps.filter(step => {
                        const stepSection = typeof step.section === 'string' ? step.section : step.section;
                        return stepSection === currentSection;
                      });
                      
                      // Check if this is the last step in the section
                      const isLastStepInSection = sectionSteps.findIndex(step => step.id === currentStepData.id) === sectionSteps.length - 1;
                      
                      // Special handling for specific sections that need completion status updates
                      const isSpecialSection = 
                        currentSection === 'ask-ana' || 
                        currentSection === 'fix-by-ana' ||
                        currentSection === 'integrations' || // Handle both naming conventions
                        currentSection === 'fix' ||          // Handle both naming conventions
                        currentSection === 'ask';            // Handle both naming conventions
                      
                      // Log for debugging
                      log(`Section: ${currentSection}, Is last step: ${isLastStepInSection}, Is special section: ${isSpecialSection}`);
                      
                      if (isLastStepInSection || 
                          (isSpecialSection && 
                           (currentStepData.id === 'ask-ana-history' || 
                            currentStepData.id === 'integrations-navigation' || 
                            currentStepData.id === 'sast-fix-buttons'))) {
                        // If this is the last step in the section or a special case, mark it as complete
                        log(`Completing section: ${currentSection}`);
                        handleComplete(currentSection);
                      } else {
                        // Otherwise, proceed to the next step
                        handleNext();
                      }
                    }}
                    variant="contained"
                    size="small"
                    endIcon={(() => {
                      // Get current section
                      const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
                      
                      // Filter steps by current section
                      const sectionSteps = steps.filter(step => {
                        const stepSection = typeof step.section === 'string' ? step.section : step.section;
                        return stepSection === currentSection;
                      });
                      
                      // Check if this is the last step in the section
                      const isLastStepInSection = sectionSteps.findIndex(step => step.id === currentStepData.id) === sectionSteps.length - 1;
                      
                      return isLastStepInSection ? <CheckCircleIcon /> : <ArrowForwardIcon />;
                    })()}
                    sx={{
                      backgroundColor: (() => {
                        // Get current section
                        const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
                        
                        // Filter steps by current section
                        const sectionSteps = steps.filter(step => {
                          const stepSection = typeof step.section === 'string' ? step.section : step.section;
                          return stepSection === currentSection;
                        });
                        
                        // Check if this is the last step in the section
                        const isLastStepInSection = sectionSteps.findIndex(step => step.id === currentStepData.id) === sectionSteps.length - 1;
                        
                        return isLastStepInSection ? '#22C55E' : '#FF4405';
                      })(),
                      color: 'white',
                      fontSize: '0.75rem',
                      px: 2,
                      '&:hover': {
                        backgroundColor: (() => {
                          // Get current section
                          const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
                          
                          // Filter steps by current section
                          const sectionSteps = steps.filter(step => {
                            const stepSection = typeof step.section === 'string' ? step.section : step.section;
                            return stepSection === currentSection;
                          });
                          
                          // Check if this is the last step in the section
                          const isLastStepInSection = sectionSteps.findIndex(step => step.id === currentStepData.id) === sectionSteps.length - 1;
                          
                          return isLastStepInSection ? '#16A34A' : '#E63E04';
                        })()
                      }
                    }}
                  >
                    {(() => {
                      // Get current section
                      const currentSection = typeof currentStepData.section === 'string' ? currentStepData.section : currentStepData.section;
                      
                      // Filter steps by current section
                      const sectionSteps = steps.filter(step => {
                        const stepSection = typeof step.section === 'string' ? step.section : step.section;
                        return stepSection === currentSection;
                      });
                      
                      // Check if this is the last step in the section
                      const isLastStepInSection = sectionSteps.findIndex(step => step.id === currentStepData.id) === sectionSteps.length - 1;
                      
                      return isLastStepInSection ? 'Finish Section' : 'Next';
                    })()}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </TooltipPopper>
      )}

      {/* Main walkthrough dialog for steps without highlights */}
      {(currentStepData.skipHighlight || !currentStepData.targetSelector) && (
        <StyledDialog
          open={showWalkthrough}
          onClose={closeWalkthrough}
          maxWidth={false}
          hideBackdrop
          sx={{ 
            zIndex: 10001,
            '& .MuiDialog-paper': {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: currentStepData.id === 'welcome' ? '600px' : '400px', // Wider dialog for welcome screen
              maxWidth: '90vw',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '80vh' // Ensure dialog doesn't exceed viewport height
            }
          }}
        >
          {/* Fixed Header - Outside DialogContent */}
          <Box sx={{ 
            p: 2, 
            borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            flexShrink: 0 // Prevent header from shrinking
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: '8px', 
                  backgroundColor: '#FF440520',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FF4405'
                }}>
                  {currentStep === steps.length - 1 ? <CheckCircleIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {currentStepData.title}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={closeWalkthrough} sx={{ color: '#94A3B8' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Scrollable Content */}
          <DialogContent sx={{ 
            p: 0, 
            overflowY: 'auto', // Enable vertical scrolling
            flexGrow: 1 // Allow content to grow and take available space
          }}>
            {/* Content */}
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" sx={{ 
                color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151',
                mb: 2,
                lineHeight: 1.5,
                fontSize: '0.9rem'
              }}>
                {currentStepData.description}
              </Typography>

              {/* Section cards for welcome step */}
              {currentStepData.id === 'welcome' && currentStepData.uniqueSections && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151',
                  }}>
                    Choose a section to explore:
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                    gap: 2.5
                  }}>
                    {currentStepData.uniqueSections.map((section, index) => {
                      // Define different colors for each section
                      const sectionColors = {
                        'dashboard-projects': { bg: '#4338CA', light: '#4338CA20', gradient: 'linear-gradient(135deg, #4338CA, #6366F1)' },
                        'integration': { bg: '#0EA5E9', light: '#0EA5E920', gradient: 'linear-gradient(135deg, #0EA5E9, #38BDF8)' },
                        'scan-using-ana': { bg: '#10B981', light: '#10B98120', gradient: 'linear-gradient(135deg, #10B981, #34D399)' },
                        'fix-by-ana': { bg: '#F59E0B', light: '#F59E0B20', gradient: 'linear-gradient(135deg, #F59E0B, #FBBF24)' },
                        'ask-ana': { bg: '#EC4899', light: '#EC489920', gradient: 'linear-gradient(135deg, #EC4899, #F472B6)' }
                      };
                      
                      // Get color based on section ID or fallback to index-based color
                      const fallbackColors = [
                        { bg: '#4338CA', light: '#4338CA20', gradient: 'linear-gradient(135deg, #4338CA, #6366F1)' }, // Indigo
                        { bg: '#0EA5E9', light: '#0EA5E920', gradient: 'linear-gradient(135deg, #0EA5E9, #38BDF8)' }, // Sky
                        { bg: '#10B981', light: '#10B98120', gradient: 'linear-gradient(135deg, #10B981, #34D399)' }, // Emerald
                        { bg: '#F59E0B', light: '#F59E0B20', gradient: 'linear-gradient(135deg, #F59E0B, #FBBF24)' }, // Amber
                        { bg: '#EC4899', light: '#EC489920', gradient: 'linear-gradient(135deg, #EC4899, #F472B6)' }  // Pink
                      ];
                      
                      const color = sectionColors[section.id as keyof typeof sectionColors] || fallbackColors[index % fallbackColors.length];
                      
                      // Check section status from API response
                      let sectionStatus = 'not_started';
                      
                      if (tourStatus) {
                        // Map section IDs to their corresponding status fields in API response
                        const statusMapping: Record<string, string> = {
                          'dashboard-projects': 'tour_dashboard_status',
                          'integration': 'tour_integration_status',
                          'scan-using-ana': 'tour_scan_status',
                          'fix-by-ana': 'tour_fix_status',
                          'ask-ana': 'tour_askana_status'
                        };
                        
                        const statusField = statusMapping[section.id] as keyof GuidedTourStatus;
                        if (statusField && tourStatus[statusField]) {
                          sectionStatus = tourStatus[statusField];
                        }
                      }
                      
                      const isCompleted = sectionStatus === 'completed';
                      
                      // Get the appropriate icon based on section ID - using the same icons as in sidebar
                      const getSectionIcon = (sectionId: string) => {
                        switch(sectionId) {
                          case 'dashboard-projects':
                            return <Folder sx={{ color: 'white', fontSize: 24 }} />;
                          case 'integration':
                            return <Hub sx={{ color: 'white', fontSize: 24 }} />;
                          case 'scan-using-ana':
                            return <Extension sx={{ color: 'white', fontSize: 24 }} />;
                          case 'fix-by-ana':
                            return <Code sx={{ color: 'white', fontSize: 24 }} />;
                          case 'ask-ana':
                            return <ForumIcon sx={{ color: 'white', fontSize: 24 }} />;
                          default:
                            return <Dashboard sx={{ color: 'white', fontSize: 24 }} />;
                        }
                      };

                      return (
                        <Paper 
                          key={section.id}
                          onClick={() => {
                            // Set selected section
                            setSelectedSection(section.id as WalkthroughSection);
                            
                            // Find the first step of the selected section directly from the main steps array
                            const sectionSteps = steps.filter(step => {
                              const stepSection = typeof step.section === 'string' ? step.section : step.section;
                              return stepSection === section.id;
                            });
                            
                            log(`Found ${sectionSteps.length} steps for section: ${section.id}`);
                            log('Section steps:', sectionSteps.map(s => s.id));
                            
                            if (sectionSteps.length > 0) {
                              // Find the index of the first step in the main steps array
                              const firstSectionStep = sectionSteps[0];
                              const stepIndex = steps.findIndex(step => step.id === firstSectionStep.id);
                              
                              if (stepIndex !== -1) {
                                log(`Starting tour for section: ${section.id} at step index: ${stepIndex} (${firstSectionStep.id})`);
                                
                                // Set the current step to the first step of this section
                                setCurrentStep(stepIndex);
                                
                                // Mark this section as started in localStorage
                                localStorage.setItem(`section-${section.id}-started`, 'true');
                                
                                // Update API status
                                handleInProgress(section.id);
                              } else {
                                error(`Could not find step index for: ${firstSectionStep.id}`);
                              }
                            } else {
                              error(`No steps found for section: ${section.id}`);
                            }
                          }}
                          sx={{ 
                            p: 0, 
                            borderRadius: '16px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            border: 'none',
                            boxShadow: theme.palette.mode === 'dark' ? 
                              `0 8px 20px -5px rgba(0, 0, 0, 0.4)` : 
                              `0 8px 20px -5px rgba(0, 0, 0, 0.15)`,
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: theme.palette.mode === 'dark' ? 
                                `0 15px 30px -8px rgba(0, 0, 0, 0.5), 0 0 0 1px ${color.bg}40` : 
                                `0 15px 30px -8px rgba(0, 0, 0, 0.2), 0 0 0 1px ${color.bg}40`
                            }
                          }}
                        >
                          {/* Card header with gradient background */}
                          <Box sx={{
                            background: color.gradient,
                            p: 1.8,
                            position: 'relative',
                            overflow: 'hidden',
                            height: '70px',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 70%)',
                              zIndex: 0
                            }
                          }}>
                            {/* Completion indicator */}
                            {isCompleted && (
                              <Box sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8,
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                              }}>
                                <CheckCircleIcon sx={{ 
                                  color: '#22C55E', 
                                  fontSize: 18,
                                  filter: 'drop-shadow(0 1px 2px rgba(34, 197, 94, 0.3))'
                                }} />
                              </Box>
                            )}
                            
                            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(4px)'
                              }}>
                                {getSectionIcon(section.id)}
                              </Box>
                              
                              <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '0.85rem',
                                color: '#FFFFFF',
                                textShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}>
                                {section.title}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Card content */}
                          <Box sx={{ 
                            p: 1.8, 
                            backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
                            height: '90px',
                          }}>
                            <Typography variant="body2" sx={{ 
                              color: theme.palette.mode === 'dark' ? '#CBD5E1' : '#4B5563',
                              mb: 1.5,
                              lineHeight: 1.4,
                              fontSize: '0.8rem',
                              height: '2.4rem',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {section.description}
                            </Typography>
                            
<Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mt: 0.5
                              }}>
                              <Box 
                                onClick={() => {
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: color.bg,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                Start Tour <ArrowForwardIcon sx={{ fontSize: 14, ml: 0.5 }} />
                              </Box>
                              
                              {(() => {
                                // Get status from API response
                                let status = 'not_started';
                                
                                if (tourStatus) {
                                  // Map section IDs to their corresponding status fields
                                  const statusMapping: Record<string, keyof GuidedTourStatus> = {
                                    'dashboard-projects': 'tour_dashboard_status',
                                    'integration': 'tour_integration_status',
                                    'scan-using-ana': 'tour_scan_status',
                                    'fix-by-ana': 'tour_fix_status',
                                    'ask-ana': 'tour_askana_status'
                                  };
                                  
                                  const statusField = statusMapping[section.id];
                                  if (statusField && tourStatus[statusField]) {
                                    status = tourStatus[statusField];
                                  }
                                }
                                
                                if (status === 'completed' || isCompleted) {
                                  return (
                                    <Typography variant="caption" sx={{ 
                                      color: '#22C55E',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      fontSize: '0.7rem'
                                    }}>
                                      <CheckCircleIcon sx={{ fontSize: 12, mr: 0.5 }} /> Completed
                                    </Typography>
                                  );
                                } else if (status === 'in_progress') {
                                  return (
                                    <Typography variant="caption" sx={{ 
                                      color: '#F59E0B',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      fontSize: '0.7rem'
                                    }}>
                                      <PlayArrowIcon sx={{ fontSize: 12, mr: 0.5 }} /> In Progress
                                    </Typography>
                                  );
                                } else if (status === 'skipped') {
                                  return (
                                    <Typography variant="caption" sx={{ 
                                      color: '#6B7280',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      fontSize: '0.7rem'
                                    }}>
                                      Skipped
                                    </Typography>
                                  );
                                } else {
                                  return (
                                    <Typography variant="caption" sx={{ 
                                      color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                                      fontWeight: 500,
                                      fontSize: '0.7rem'
                                    }}>
                                      Not started
                                    </Typography>
                                  );
                                }
                              })()}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                </Box>
              )}

            </Box>

          </DialogContent>
        </StyledDialog>
      )}

      {/* Trial Expired Dialog - Only shown when not in isTrialExpired mode */}
      {!isTrialExpired && (
        <TrialExpiredDialog
          open={showTrialExpiredDialog}
          onClose={() => {
            setShowTrialExpiredDialog(false);
            closeWalkthrough(); // Close the walkthrough when dialog is closed
          }}
          onLearnMore={() => {
            setShowTrialExpiredDialog(false);
            setShowUpgradeDialog(true);
          }}
        />
      )}

      {/* Upgrade Dialog */}
      <UpgradeDialog 
        open={showUpgradeDialog} 
        onClose={() => {
          setShowUpgradeDialog(false);
          closeWalkthrough(); // Close walkthrough when upgrade dialog is closed
        }} 
      />
    </>
  );
};

export default GuidedWalkthrough;
